import React, { createContext, useState, useEffect, useCallback } from 'react';
import {
  fetchUserProfile,
} from '../api/profile';
import { logoutUser } from '../api/auth';

// Helper functions for handling cookies
const setCookie = (name, value, days = 1, path = "/", options = {}) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000); // Convert days to milliseconds
  let cookieString = `${name}=${value}; expires=${expires.toUTCString()}; path=${path}`;

  if (options.secure) {
    cookieString += '; Secure';
  }
  if (options.sameSite) {
    cookieString += `; SameSite=${options.sameSite}`;
  }
  // HttpOnly cannot be set via JavaScript, must be set by server

  document.cookie = cookieString;
//  console.log(`Cookie set: ${cookieString}`); // Debug log
};

export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
};
const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Lax`;
};

// Create Auth Context
export const AuthContext = createContext({
  currentStep: 1,
  setCurrentStep: () => { },
  accountNature: '',
  setAccountNature: () => { },
  kycCompleted: false,
  setKycCompleted: () => { },
  user: null,
  loading: true,
  error: null,
  authlogin: () => { },
  logout: () => { },
});

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [accountNature, setAccountNature] = useState('');
  const [kycCompleted, setKycCompleted] = useState(false);
  const [error, setError] = useState(null);

  // Validate the access token
  const validateAccessToken = useCallback((access_token) => {
    if (!access_token) return false;
    try {
      const payload = JSON.parse(atob(access_token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch (err) {
      console.error('Error validating access token:', err);
      return false;
    }
  }, []);

  // Refresh the access token
  const refreshToken = useCallback(async () => {
    try {
      const refresh_token = getCookie('refresh_token');
      if (!refresh_token) throw new Error('No refresh token');

      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refresh_token}`,
        },
      });

      if (!response.ok) throw new Error('Token refresh failed');

      const data = await response.json();
      setCookie('access_token', data.access_token, 1);
      setCookie('refresh_token', data.refresh_token, 7);
      return data.access_token;
    } catch (err) {
      console.error('Error refreshing token:', err);
      logout();
      throw err;
    }
  }, []);

  // Initialize authentication
  const initializeAuth = async () => {
    try {
     // console.log('Initializing auth...');
      const access_token = getCookie('access_token');
      const user_id = localStorage.getItem('user_id');
      const account_nature = localStorage.getItem('account_nature');
      const kycCompletedStorage = localStorage.getItem('kyc_completed');

      if (access_token && user_id) {
        let validAccessToken = access_token;
        if (!validateAccessToken(access_token)) {
      //    console.log('Access token expired. Refreshing token...');
          validAccessToken = await refreshToken();
          setCookie('access_token', validAccessToken, 1);
        }

        setUser({ access_token: validAccessToken, user_id });

        const userProfile = await fetchUserProfile(user_id);
     //   console.log('Fetched user profile:', userProfile);

        if (userProfile) {
          const isKycCompleted = userProfile.user.kyc_completed;
          const userCurrentStep = userProfile.user.current_step || 1;
          const userAccountNature = userProfile.user.account_nature || account_nature || '';
        //  console.log('KYC status:', isKycCompleted);
          setKycCompleted(isKycCompleted);
          localStorage.setItem('kyc_completed', isKycCompleted);
          setCurrentStep(userCurrentStep);
          setAccountNature(userAccountNature);
       //   console.log('Setting currentStep from user profile:', userCurrentStep);
       //   console.log('Setting accountNature from user profile:', userAccountNature);
        } else {
          setCurrentStep(1);
          setAccountNature(account_nature || '');
        }

      } else {
      //  console.log('No access token or user ID found. User is not logged in.');
      }
    } catch (err) {
      console.error('Error during initialization:', err);
      setError(err.message);
    } finally {
    //  console.log('Initialization complete.');
      setLoading(false);
    }
  };

  // Initialize auth on component mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Login function
  const authlogin = async (
    access_token,
    user_id,
    refresh_token,
    kyc_completed,
    email,
    is_verified,
    account_nature,
    role,
    current_step
  ) => {
    try {
      if (!access_token || !user_id || !refresh_token) {
        throw new Error('Invalid login data');
      }

      // console.log('Logging in with:', {
      //   access_token,
      //   user_id,
      //   refresh_token,
      //   kyc_completed,
      //   current_step,
      // });

      setCookie('access_token', access_token, 1);
      setCookie('refresh_token', refresh_token, 7);
      localStorage.setItem('user_id', user_id);
      localStorage.setItem('kyc_completed', kyc_completed);
      localStorage.setItem('email', email);
      localStorage.setItem('is_verified', is_verified);
      localStorage.setItem('account_nature', account_nature);
      localStorage.setItem('role', role);
      console.log('Cookies after login:', document.cookie); // Debug log

      // Set cookies with security flags
      setCookie('access_token', access_token, 1, '/', { secure: true, sameSite: 'Lax' });
      setCookie('refresh_token', refresh_token, 7, '/', { secure: true, sameSite: 'Lax' });
      // Set a session cookie
      setCookie('session_id', user_id, 1, '/', { secure: true, sameSite: 'Lax' });

      setUser({ access_token, user_id });
      setKycCompleted(kyc_completed);
      setError(null);

      setCurrentStep(current_step);
      setAccountNature(account_nature);
      // console.log("current step in authcontext is ", current_step);
      // console.log("account nature in authcontext is ", account_nature);

    } catch (err) {
   //   console.error('Error during login:', err);
      setError(err.message);
      throw err;
    }
  };

  // Logout function
  // Fire-and-forget server notification: the /api/user/logout endpoint deletes
  // the refresh token from the auth server, logs the user out of the trading
  // server, and deactivates the local trading session. The local logout always
  // succeeds even if the server call fails, so the API errors are swallowed.
  const logout = () => {
    try {
      console.log('Logging out...');

      // Fire the server logout with the refresh token (fire-and-forget).
      // Errors are intentionally ignored so local logout always completes.
      logoutUser()
        .then(() => console.log('Server logout completed.'))
        .catch((err) => {
          console.warn('Server logout failed; proceeding with local logout:', err);
        });

      deleteCookie('access_token');
      deleteCookie('refresh_token');
      deleteCookie('session_id');
      localStorage.removeItem('user_id');
      localStorage.removeItem('kyc_completed');
      localStorage.removeItem('email');
      localStorage.removeItem('is_verified');
      localStorage.removeItem('account_nature');
      localStorage.removeItem('role');
      setUser(null);
      setError(null);
    } catch (err) {
      console.error('Error during logout:', err);
      setError(err.message);
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        currentStep,
        setCurrentStep,
        accountNature,
        setAccountNature,
        kycCompleted,
        setKycCompleted,
        authlogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
